import React, { useState, useEffect, useRef } from 'react';
import { Play, Trophy, Timer, Target, Zap } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { Settings } from './types';
import { startProgressAssessment, getTargetTime, getProgressMetrics } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import CountdownAnimation from '../ui/CountdownAnimation';

interface SelectionScreenProps {
  onStart: (settings: Settings) => void;
  onBestCQPMLoaded?: (bestCQPM: number | null) => void;
}

const SelectionScreen: React.FC<SelectionScreenProps> = ({ onStart, onBestCQPMLoaded }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [questions, setQuestions] = useState<Settings['questions']>([]);
  const [assessmentId, setAssessmentId] = useState<string>('');
  const [timePerDigit, setTimePerDigit] = useState<number | null>(null);
  const [targetTime, setTargetTime] = useState<number | null>(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const [bestCQPM, setBestCQPM] = useState<number | null>(null);
  const audioPlayedRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.userId) return;

      setIsFetching(true);
      try {
        // Fetch target time, assessment data, and metrics in parallel
        // Metrics is non-critical — if it fails, treat as no previous best
        const [timeResponse, assessmentResponse, metricsResponse] = await Promise.all([
          getTargetTime(user.userId),
          startProgressAssessment(user.userId),
          getProgressMetrics(user.userId).catch(() => ({ bestCQPM: null }))
        ]);

        if (!assessmentResponse.questions || !assessmentResponse.assessmentId) {
          throw new Error('Invalid response from server');
        }

        setTimePerDigit(timeResponse.timePerDigit);
        setTargetTime(timeResponse.targetTime);
        setQuestions(assessmentResponse.questions);
        setAssessmentId(assessmentResponse.assessmentId);
        setBestCQPM(metricsResponse.bestCQPM);
        onBestCQPMLoaded?.(metricsResponse.bestCQPM);

        // Play voiceover after data loads
        if (!audioPlayedRef.current) {
          audioPlayedRef.current = true;
          const audioFile = metricsResponse.bestCQPM !== null
            ? '/assets/audio/progressAssessment/selection_has_best.mp3'
            : '/assets/audio/progressAssessment/selection_no_best.mp3';
          const audio = new Audio(audioFile);
          audio.play().catch(() => {}); // Silently handle autoplay restrictions
        }
      } catch (error) {
        console.error('Failed to fetch assessment data:', error);
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, [user?.userId]);

  // Add keyboard event listener for Enter key
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && 
          !isLoading && 
          !isFetching && 
          questions.length > 0 && 
          assessmentId && 
          timePerDigit !== null && 
          targetTime !== null) {
        handleStart();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [questions, isLoading, isFetching, assessmentId, timePerDigit, targetTime]);

  const handleStart = () => {
    if (questions.length === 0 || !assessmentId || timePerDigit === null || targetTime === null) return;
    setIsLoading(true);
    setShowCountdown(true);
  };

  const handleCountdownComplete = () => {
    try {
      if (questions.length === 0 || !assessmentId || timePerDigit === null || targetTime === null) return;

      const params = new URLSearchParams(window.location.search);
      const inputMethod = params.get('voiceMode') === 'true' ? 'voice' : 'typing';

      onStart({
        inputMethod,
        questions,
        assessmentId,
        timePerDigit,
        baseTime: targetTime
      });
    } catch (error) {
      console.error('Failed to start progress assessment:', error);
      setIsLoading(false);
      setShowCountdown(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-indigo-50 flex flex-col justify-center p-6">
      {/* Full-screen loading overlay */}
      <AnimatePresence>
        {isFetching && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center"
          >
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-lg text-gray-600">Loading...</p>
          </motion.div>
        )}
      </AnimatePresence>

      {showCountdown && (
        <CountdownAnimation onComplete={handleCountdownComplete} />
      )}

      {!isFetching && (
        <div className="max-w-md mx-auto space-y-6">
          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-center"
          >
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
              Assessment
            </span>
          </motion.h1>

          {/* Best Score Hero */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
            className="relative overflow-hidden rounded-2xl p-8 text-center bg-white border border-indigo-100 shadow-lg shadow-indigo-100/50"
          >
            {/* Decorative corner accents */}
            <div className="absolute top-0 right-0 w-28 h-28 bg-gradient-to-bl from-indigo-50 to-transparent rounded-bl-full" />
            <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-purple-50 to-transparent rounded-tr-full" />

            {bestCQPM !== null ? (
              <div className="relative">
                <Trophy className="w-11 h-11 text-yellow-500 mx-auto mb-2" />
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-1">
                  Best Score
                </p>
                <span className="block text-7xl font-black bg-gradient-to-b from-indigo-500 to-purple-600 text-transparent bg-clip-text leading-tight">
                  {bestCQPM}
                </span>
                <p className="text-base font-bold text-indigo-500 mt-3">
                  Can you beat it?
                </p>
              </div>
            ) : (
              <div className="relative">
                <Trophy className="w-11 h-11 text-indigo-200 mx-auto mb-2" />
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-1">
                  Best Score
                </p>
                <span className="block text-4xl font-black text-indigo-200 leading-tight">
                  --
                </span>
                <p className="text-base font-bold text-indigo-500 mt-3">
                  Set your first high score!
                </p>
              </div>
            )}
          </motion.div>

          {/* Info Pills */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-center gap-2.5 flex-wrap"
          >
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-indigo-100 text-indigo-600 text-sm font-medium shadow-sm">
              <Timer className="w-3.5 h-3.5" />
              2 minutes
            </span>
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-indigo-100 text-indigo-600 text-sm font-medium shadow-sm">
              <Target className="w-3.5 h-3.5" />
              Be fast & accurate
            </span>
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-indigo-100 text-indigo-600 text-sm font-medium shadow-sm">
              <Zap className="w-3.5 h-3.5 text-yellow-500" />
              Earns 2XP
            </span>
          </motion.div>

          {/* Start Button */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="pt-1"
          >
            <motion.button
              whileHover={{ scale: 1.03, boxShadow: '0 10px 40px -10px rgba(99, 102, 241, 0.5)' }}
              whileTap={{ scale: 0.97 }}
              onClick={handleStart}
              disabled={isLoading}
              className={`w-full px-8 py-5 bg-gradient-to-r from-indigo-500 to-purple-500
                text-white rounded-2xl font-bold text-xl shadow-lg shadow-indigo-200/50
                transition-all duration-300 flex items-center justify-center gap-3
                ${isLoading && 'opacity-50 cursor-not-allowed'}`}
            >
              <span>{isLoading ? 'Starting...' : 'Start Assessment'}</span>
              {!isLoading && <Play className="w-6 h-6 fill-white" />}
            </motion.button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SelectionScreen;